import { components } from "@api/api";

export type Slice = {
    id: number;
    name: string;
    isTableSlice?: boolean;
    columns?: SlicesEnum[];
    columnsOrder?: (number | string)[];
    visibleForCountries?: string[];
};

export enum SlicesEnum {
    None = -1,
    Segments = 1000,
    HostedLocations = 1018,
    RestaurantTypes = 1001,
    Regions = 1002,
    Cuisines = 1003,
    DayParts = 1004,
    MenuParts = 1005,
    ItemTypes = 1006,
    Ethnic = 1007,
    NonEthnic = 1008,
    Total = 1009,
    Period = 1010,
    PredictPeriod = 1011,
    OneYearTrend = 1012,
    FourYearTrend = 1013,
    LongTermTrends = 1014,
    FoodTrucks = 1015,
    Restaurants = 1016,
    MenuItemAttribute = 1017,
    PeriodQuarter = 1019,
    PredictPeriodQuarter = 1020,
    PredictedFourYearTrend = 1021,
    PastGrowth = 9997,
    PredictedGrowth = 9998,
    Consumers = 9999,
}

export const ORIGINAL_GROWTH_SLICES = [
    SlicesEnum.OneYearTrend,
    SlicesEnum.FourYearTrend,
    SlicesEnum.PredictedFourYearTrend,
];

export const CUSTOM_GROWTH_SLICES = [
    SlicesEnum.PastGrowth,
    SlicesEnum.PredictedGrowth,
];

export const GROWTH_SLICES = [
    ...ORIGINAL_GROWTH_SLICES,
    ...CUSTOM_GROWTH_SLICES,
];

export enum SegmentsEnum {
    QSR = 1,
    FastCasual = 28,
    Midscale = 2,
    CasualDining = 3,
    FineDining = 4,
    Schools = 7,
    CnU = 6,
    Hospital = 5,
    LongTermCare = 9,
    Restaurants = 56,
}

export enum RegionsEnum {
    West = 1,
    Midwest = 2,
    South = 3,
    NorthEast = 4,
}

export enum ConsumersEnum {
    KnowIt = 1,
    HaveTriedIt = 2,
    LoveOrLikeIt = 3,
}

enum ItemTypesEnum {
    AppetizerSoupChili = 1,
    AppetizerSalad = 2,
    DeliSaladAppetizer = 3,
    AppetizerFries = 4,
    PotatoSkins = 5,
    FriedProteinApp = 6,
    FriedVeggieApp = 7,
    FriedOtherApp = 8,
    NonFriedProteinApp = 9,
    NonFriedVeggieApp = 10,
    Wings = 11,
    AppetizerNachos = 12,
    AppetizerQuesadilla = 13,
    AppetizerDip = 14,
    BreadAppetizer = 15,
    Sampler = 16,
    OtherApp = 17,
    HotSandwich = 18,
    ColdSandwich = 19,
    Burger = 20,
    HotDogSausage = 21,
    SaladEntree = 22,
    Pasta = 23,
    BeefMainEntree = 24,
    ChickenMainEntree = 25,
    FishMainEntree = 26,
    ShellfishMainEntree = 27,
    PorkMainEntree = 28,
    OtherProteinMainEntree = 29,
    VeggieMainEntree = 30,
    HandheldProtein = 31,
    RiceEntree = 32,
    Pizza = 33,
    MexicanEntree = 34,
    BreakfastStarch = 35,
    EggDish = 36,
    EntreeStewSoupChili = 37,
    CombosMultiProtein = 38,
    OtherEntree = 39,
    SideSoupChili = 40,
    SaladSide = 41,
    RiceSide = 42,
    FriesSide = 43,
    MashedPotato = 44,
    BakedPotato = 45,
    OtherPotatoSide = 46,
    FriedVeggieSide = 47,
    NonFriedVeggieSide = 48,
    ProteinSide = 49,
    FruitSide = 50,
    Beans = 51,
    CondimentTopping = 52,
    BreadSide = 53,
    DeliSaladSide = 54,
    OtherSide = 55,
    DessertBar = 56,
    BreadPudding = 57,
    BreakfastBakery = 58,
    Brownie = 59,
    Cake = 60,
    CheesePlatter = 61,
    Cheesecake = 62,
    CobblerCrisp = 63,
    Cookie = 64,
    CremeBrulee = 65,
    CustardFlan = 66,
    DessertBeverage = 67,
    Ethnic_Dessert = 68,
    FrozenTreat = 69,
    FruitDessert = 70,
    GenericDessert = 71,
    MixedDessert = 72,
    OtherDessert = 73,
    Pastry = 74,
    Pie = 75,
    PuddingJello = 76,
    Sundae = 77,
    Tiramisu = 78,
    BottledAlcohol = 79,
    Beer = 80,
    BottledWater = 81,
    CSD = 82,
    SparklingWine = 83,
    Coffee = 84,
    EnergyDrink = 85,
    HotChocolate = 86,
    HotTea = 87,
    BlendedDrink = 88,
    IcedCoffee = 89,
    IcedTea = 90,
    Juice = 91,
    Lemonade = 92,
    Margarita = 93,
    Martini = 94,
    Milk = 95,
    CocktailMixedDrink = 96,
    OtherNonFizzyDrink = 97,
    Other = 98,
    PremiumCoffee = 99,
    PremiumIcedCoffee = 100,
    SportsDrink = 101,
    Wine = 102,
    HardSeltzer = 103,
    HardCider = 104,
    OtherAdultBev = 105,
    OtherNaBev = 106,
}

enum CuisinesEnum {
    BBQ = 4,
    Burger = 5,
    CoffeeAndBakery = 9,
    DessertAndSnack = 13,
    Pizza = 19,
    Sandwich = 20,
    Seafood = 21,
    Steakhouse = 24,
    American = 2,
    AmericanSouthern = 23,
    Caribbean = 6,
    CentralAndSoAmerica = 22,
    Mexican = 18,
    Chinese = 7,
    Indian = 14,
    Japanese = 16,
    Korean = 17,
    Thai = 25,
    OtherAsian = 3,
    French = 11,
    Mediterranean = 12,
    Italian = 15,
    OtherEuropean = 10,
    African = 1,
    MixedEthnicity = 8,
}

export const SLICES: Slice[] = [
    {
        id: SlicesEnum.Segments,
        name: 'Segments',
        isTableSlice: true,
        columns: [
            SlicesEnum.Total,
            SlicesEnum.Segments,
            ...GROWTH_SLICES,
            SlicesEnum.HostedLocations,
            SlicesEnum.Restaurants,
        ],
        columnsOrder: [
            SegmentsEnum.QSR,
            SegmentsEnum.FastCasual,
            SegmentsEnum.Midscale,
            SegmentsEnum.CasualDining,
            SegmentsEnum.FineDining,
            SegmentsEnum.Schools,
            SegmentsEnum.CnU,
            SegmentsEnum.Hospital,
            SegmentsEnum.LongTermCare,
            SegmentsEnum.Restaurants,
            'rest', /* items which order doesn't matter */
        ],
    },
    {
        id: SlicesEnum.HostedLocations,
        name: 'Segments', /* Hosted locations */
        isTableSlice: true,
        columns: [
            SlicesEnum.Total,
            SlicesEnum.HostedLocations,
            SlicesEnum.Restaurants,
        ],
    },
    {
        id: SlicesEnum.RestaurantTypes,
        name: 'Restaurant types',
        isTableSlice: true,
        columns: [
            SlicesEnum.Total,
            SlicesEnum.RestaurantTypes,
            ...GROWTH_SLICES,
            SlicesEnum.FoodTrucks, /* US Innovators */
        ],
        columnsOrder: [
            3,
            2,
            1,
            'rest', /* items which order doesn't matter */
        ],
    },
    {
        id: SlicesEnum.Regions,
        name: 'Regions',
        isTableSlice: true,
        columns: [
            SlicesEnum.Total,
            SlicesEnum.Regions,
            ...GROWTH_SLICES,
        ],
        columnsOrder: [
            'rest', /* items which order doesn't matter */
        ],
    },
    {
        id: SlicesEnum.Cuisines,
        name: 'Cuisines',
        isTableSlice: true,
        columns: [
            SlicesEnum.Total,
            SlicesEnum.Cuisines,
            ...GROWTH_SLICES,
        ],
        columnsOrder: [
            SlicesEnum.NonEthnic,
            SlicesEnum.Ethnic,
            CuisinesEnum.BBQ,
            CuisinesEnum.Burger,
            CuisinesEnum.CoffeeAndBakery,
            CuisinesEnum.DessertAndSnack,
            CuisinesEnum.Pizza,
            CuisinesEnum.Sandwich,
            CuisinesEnum.Seafood,
            CuisinesEnum.Steakhouse,
            CuisinesEnum.American,
            CuisinesEnum.AmericanSouthern,
            CuisinesEnum.Caribbean,
            CuisinesEnum.CentralAndSoAmerica,
            CuisinesEnum.Mexican,
            CuisinesEnum.Chinese,
            CuisinesEnum.Indian,
            CuisinesEnum.Japanese,
            CuisinesEnum.Korean,
            CuisinesEnum.Thai,
            CuisinesEnum.OtherAsian,
            CuisinesEnum.French,
            CuisinesEnum.Mediterranean,
            CuisinesEnum.Italian,
            CuisinesEnum.OtherEuropean,
            CuisinesEnum.African,
            CuisinesEnum.MixedEthnicity,
            'rest', /* items which order doesn't matter */
        ],
    },
    {
        id: SlicesEnum.DayParts,
        name: 'Day parts',
        isTableSlice: true,
        columns: [
            SlicesEnum.Total,
            SlicesEnum.DayParts,
            ...GROWTH_SLICES,
        ],
        columnsOrder: [
            'rest', /* items which order doesn't matter */
        ],
    },
    {
        id: SlicesEnum.MenuParts,
        name: 'Menu parts',
        isTableSlice: true,
        columns: [
            SlicesEnum.Total,
            SlicesEnum.MenuParts,
            ...GROWTH_SLICES,
        ],
        columnsOrder: [
            'rest', /* items which order doesn't matter */
        ],
    },
    {
        id: SlicesEnum.ItemTypes,
        name: 'Item types',
        isTableSlice: true,
        columns: [
            SlicesEnum.Total,
            SlicesEnum.ItemTypes,
            ...GROWTH_SLICES,
        ],
        columnsOrder: [
            ItemTypesEnum.AppetizerSalad,
            ItemTypesEnum.DeliSaladAppetizer,
            ItemTypesEnum.AppetizerSoupChili,
            ItemTypesEnum.FriedProteinApp,
            ItemTypesEnum.NonFriedVeggieApp,
            ItemTypesEnum.FriedOtherApp,
            ItemTypesEnum.NonFriedProteinApp,
            ItemTypesEnum.NonFriedVeggieSide,
            ItemTypesEnum.AppetizerDip,
            ItemTypesEnum.BreadAppetizer,
            ItemTypesEnum.AppetizerFries,
            ItemTypesEnum.PotatoSkins,
            ItemTypesEnum.AppetizerNachos,
            ItemTypesEnum.AppetizerQuesadilla,
            ItemTypesEnum.Wings,
            ItemTypesEnum.Sampler,
            ItemTypesEnum.OtherApp,
            ItemTypesEnum.HotSandwich,
            ItemTypesEnum.ColdSandwich,
            ItemTypesEnum.Burger,
            ItemTypesEnum.HotDogSausage,
            ItemTypesEnum.HandheldProtein,
            ItemTypesEnum.BeefMainEntree,
            ItemTypesEnum.ChickenMainEntree,
            ItemTypesEnum.FishMainEntree,
            ItemTypesEnum.PorkMainEntree,
            ItemTypesEnum.ShellfishMainEntree,
            ItemTypesEnum.VeggieMainEntree,
            ItemTypesEnum.OtherProteinMainEntree,
            ItemTypesEnum.MexicanEntree,
            ItemTypesEnum.Pasta,
            ItemTypesEnum.Pizza,
            ItemTypesEnum.RiceEntree,
            ItemTypesEnum.SaladEntree,
            ItemTypesEnum.EntreeStewSoupChili,
            ItemTypesEnum.BreakfastStarch,
            ItemTypesEnum.EggDish,
            ItemTypesEnum.CombosMultiProtein,
            ItemTypesEnum.OtherEntree,
            ItemTypesEnum.SaladSide,
            ItemTypesEnum.DeliSaladSide,
            ItemTypesEnum.SideSoupChili,
            ItemTypesEnum.FriesSide,
            ItemTypesEnum.BakedPotato,
            ItemTypesEnum.MashedPotato,
            ItemTypesEnum.OtherPotatoSide,
            ItemTypesEnum.Beans,
            ItemTypesEnum.FriedVeggieSide,
            ItemTypesEnum.NonFriedVeggieSide,
            ItemTypesEnum.FruitSide,
            ItemTypesEnum.BreadSide,
            ItemTypesEnum.ProteinSide,
            ItemTypesEnum.RiceSide,
            ItemTypesEnum.CondimentTopping,
            ItemTypesEnum.OtherSide,
            ItemTypesEnum.Cake,
            ItemTypesEnum.Cheesecake,
            ItemTypesEnum.Pie,
            ItemTypesEnum.CobblerCrisp,
            ItemTypesEnum.BreadPudding,
            ItemTypesEnum.CremeBrulee,
            ItemTypesEnum.CustardFlan,
            ItemTypesEnum.Tiramisu,
            ItemTypesEnum.FrozenTreat,
            ItemTypesEnum.Sundae,
            ItemTypesEnum.Brownie,
            ItemTypesEnum.Cookie,
            ItemTypesEnum.DessertBar,
            ItemTypesEnum.BreakfastBakery,
            ItemTypesEnum.Pastry,
            ItemTypesEnum.CheesePlatter,
            ItemTypesEnum.DessertBeverage,
            ItemTypesEnum.Ethnic_Dessert,
            ItemTypesEnum.FruitDessert,
            ItemTypesEnum.PuddingJello,
            ItemTypesEnum.GenericDessert,
            ItemTypesEnum.MixedDessert,
            ItemTypesEnum.OtherDessert,
            ItemTypesEnum.CSD,
            ItemTypesEnum.Juice,
            ItemTypesEnum.Lemonade,
            ItemTypesEnum.Milk,
            ItemTypesEnum.OtherNonFizzyDrink,
            ItemTypesEnum.Coffee,
            ItemTypesEnum.IcedCoffee,
            ItemTypesEnum.PremiumCoffee,
            ItemTypesEnum.PremiumIcedCoffee,
            ItemTypesEnum.HotTea,
            ItemTypesEnum.IcedTea,
            ItemTypesEnum.HotChocolate,
            ItemTypesEnum.BlendedDrink,
            ItemTypesEnum.EnergyDrink,
            ItemTypesEnum.SportsDrink,
            ItemTypesEnum.BottledWater,
            ItemTypesEnum.OtherNaBev,
            ItemTypesEnum.Other,
            ItemTypesEnum.Beer,
            ItemTypesEnum.SparklingWine,
            ItemTypesEnum.Wine,
            ItemTypesEnum.BottledAlcohol,
            ItemTypesEnum.Margarita,
            ItemTypesEnum.Martini,
            ItemTypesEnum.CocktailMixedDrink,
            ItemTypesEnum.HardSeltzer,
            ItemTypesEnum.HardCider,
            ItemTypesEnum.OtherAdultBev,
            'rest', /* items which order doesn't matter */
        ],
    },
    {
        id: SlicesEnum.Ethnic,
        name: 'ethnic',
    },
    {
        id: SlicesEnum.NonEthnic,
        name: 'nonethnic',
    },
    {
        id: SlicesEnum.Total,
        name: 'all',
    },
    {
        id: SlicesEnum.Period,
        name: 'period   ',
    },
    {
        id: SlicesEnum.PredictPeriod,
        name: 'predictPeriod',
    },
    {
        id: SlicesEnum.OneYearTrend,
        name: '1 year trend',
    },
    {
        id: SlicesEnum.FourYearTrend,
        name: '4 year trend',
    },
    {
        id: SlicesEnum.FoodTrucks,
        name: 'Food trucks',
    },
    {
        id: SlicesEnum.Restaurants,
        name: 'Restaurants',
    },
    {
        id: SlicesEnum.PeriodQuarter,
        name: 'periodQuarter',
    },
    {
        id: SlicesEnum.PredictPeriodQuarter,
        name: 'predictPeriodQuarter',
    },
    {
        id: SlicesEnum.PredictedFourYearTrend,
        name: 'predict4YearTrend',
    },
    {
        id: SlicesEnum.Consumers,
        name: 'Consumers',
        isTableSlice: true,
        visibleForCountries: ['usa'],
        columns: [
            SlicesEnum.Total,
            SlicesEnum.Consumers, /* Flavor data */
        ],
    },
];

export function getSliceName(sliceId: number | null) {
    const slice = SLICES.find(s => s.id === sliceId);
    return slice ? slice.name : '';
}

export function getIsTableSlice(sliceId: number | null) {
    const slice = SLICES.find(s => s.id === sliceId);
    return slice ? slice.isTableSlice : false;
}

export enum ByoAnalyticType {
    Penetration = 1,
    Incidence = 2,
    Price = 3,
    Count = 4,
}

export enum ByoValueType {
    Pct = 1,
    Count = 2,
}

type API__ByoWord = components["schemas"]["ByoViewModel"];
export type ByoWord = API__ByoWord & {
    keyword: NonNullable<API__ByoWord['keyword']>;
    isLoading?: boolean;
};

export const BYO_DEFAULT_SLICE_ID = SlicesEnum.Segments;
export const BYO_DEFAULT_VALUE_TYPE = ByoValueType.Pct;
export const BYO_DEFAULT_ANALYTIC_TYPE = ByoAnalyticType.Penetration

export enum ByoGrowthPeriod {
    TwelveMonth = '12 months',
    FourYear = '4 years',
}

export function getGrowthColumn(years: number): ByoGrowthPeriod {
    switch (years) {
        case 1:
            return ByoGrowthPeriod.TwelveMonth;
        case 4:
            return ByoGrowthPeriod.FourYear;
        default:
            return ByoGrowthPeriod.FourYear;
    }
}